import type { ComponentProps, ElementType } from 'react';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from 'design-system';
import { ChevronRight } from 'lucide-react';

import type { ExtraProps } from 'react-markdown';

export const MarkdownLink: ElementType<ComponentProps<'a'> & ExtraProps> = ({
  node,
  children,
  className,
  href,
  ...props
}) => {
  const gxCode = href?.match(/^\/(GX\d{7})$/)?.[1];
  const tooltip = gxCode ? `Open ${gxCode} index details` : 'Click to see index information';

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <a
            {...props}
            aria-label={tooltip}
            className={[
              className,
              gxCode
                ? 'mx-0.5 inline-flex items-center rounded-md border border-gx-highlight/45 bg-gx-highlight/10 px-1.5 py-0.5 font-mono text-[0.9em] leading-none font-semibold text-gx-highlight no-underline transition-colors hover:border-gx-highlight hover:bg-gx-highlight/20'
                : undefined
            ]
              .filter(Boolean)
              .join(' ')}
            href={href}
            rel='noopener noreferrer'
            target='_blank'
          />
        }
      >
        {children}
        {!gxCode && <ChevronRight className='size-[1em] align-text-bottom' />}
      </TooltipTrigger>
      <TooltipContent>{tooltip}</TooltipContent>
    </Tooltip>
  );
};

export const MarkdownH1: ElementType<ComponentProps<'h1'> & ExtraProps> = ({
  node,
  children,
  ...props
}) => (
  <h1
    {...props}
    className='my-6 text-xl font-bold'
  >
    {children}
  </h1>
);

export const MarkdownH2: ElementType<ComponentProps<'h2'> & ExtraProps> = ({
  node,
  children,
  ...props
}) => (
  <h2
    {...props}
    className='my-6 text-base font-bold'
  >
    {children}
  </h2>
);

export const MarkdownH3: ElementType<ComponentProps<'h3'> & ExtraProps> = ({
  node,
  children,
  ...props
}) => (
  <h3
    {...props}
    className='my-6 text-sm font-bold'
  >
    {children}
  </h3>
);

export const MarkdownH4: ElementType<ComponentProps<'h4'> & ExtraProps> = ({
  node,
  children,
  ...props
}) => (
  <h4
    {...props}
    className='my-6 text-sm font-bold'
  >
    {children}
  </h4>
);

export const MarkdownH5: ElementType<ComponentProps<'h5'> & ExtraProps> = ({
  node,
  children,
  ...props
}) => (
  <h5
    {...props}
    className='my-6 text-[13px] font-bold'
  >
    {children}
  </h5>
);

export const MarkdownH6: ElementType<ComponentProps<'h6'> & ExtraProps> = ({
  node,
  children,
  ...props
}) => (
  <h6
    {...props}
    className='my-6 text-xs font-bold'
  >
    {children}
  </h6>
);

export const MarkdownBlockquote: ElementType<ComponentProps<'blockquote'> & ExtraProps> = ({
  node,
  ...props
}) => (
  <blockquote
    {...props}
    className='my-6 border-l-4 border-border pl-4 text-muted-foreground'
  />
);

export const MarkdownCode: ElementType<ComponentProps<'code'> & ExtraProps> = ({
  node,
  ...props
}) => (
  <code
    {...props}
    className='rounded bg-muted px-2 py-1 font-mono text-[13px]'
  />
);

export const MarkdownPre: ElementType<ComponentProps<'pre'> & ExtraProps> = ({
  node,
  ...props
}) => (
  <pre
    {...props}
    className='my-6 overflow-auto rounded bg-muted p-4 font-mono text-[13px]'
  />
);

export const MarkdownHr: ElementType<ComponentProps<'hr'> & ExtraProps> = ({ node, ...props }) => (
  <hr
    {...props}
    className='my-6 border-border'
  />
);

export const MarkdownDel: ElementType<ComponentProps<'del'> & ExtraProps> = ({
  node,
  ...props
}) => (
  <del
    {...props}
    className='line-through'
  />
);

export const MarkdownTable: ElementType<ComponentProps<'table'> & ExtraProps> = ({
  node,
  ...props
}) => <Table {...props} />;

export const MarkdownThead: ElementType<ComponentProps<'thead'> & ExtraProps> = ({
  node,
  ...props
}) => <TableHeader {...props} />;

export const MarkdownTbody: ElementType<ComponentProps<'tbody'> & ExtraProps> = ({
  node,
  ...props
}) => <TableBody {...props} />;

export const MarkdownTr: ElementType<ComponentProps<'tr'> & ExtraProps> = ({ node, ...props }) => (
  <TableRow {...props} />
);

export const MarkdownImg: ElementType<ComponentProps<'img'> & ExtraProps> = ({
  node,
  alt = '',
  ...props
}) => (
  <img
    alt={alt}
    {...props}
    className='my-6 block h-auto max-w-full'
  />
);

export const MarkdownUl: ElementType<ComponentProps<'ul'> & ExtraProps> = ({ node, ...props }) => (
  <ul
    {...props}
    className='my-6 list-disc pl-6'
  />
);

export const MarkdownOl: ElementType<ComponentProps<'ol'> & ExtraProps> = ({ node, ...props }) => (
  <ol
    {...props}
    className='my-6 list-decimal pl-6'
  />
);

export const MarkdownLi: ElementType<ComponentProps<'li'> & ExtraProps> = ({ node, ...props }) => (
  <li
    {...props}
    className='mb-2 text-sm'
  />
);

export const MarkdownP: ElementType<ComponentProps<'p'> & ExtraProps> = ({ node, ...props }) => (
  <p
    {...props}
    className='my-6 text-sm leading-7'
  />
);

export const MarkdownStrong: ElementType<ComponentProps<'strong'> & ExtraProps> = ({
  node,
  ...props
}) => (
  <strong
    {...props}
    className='font-bold'
  />
);

export const MarkdownEm: ElementType<ComponentProps<'em'> & ExtraProps> = ({ node, ...props }) => (
  <em
    {...props}
    className='italic'
  />
);

const spanClassToColor: Record<string, string> = {
  muted: 'text-muted-foreground',
  error: 'text-destructive',
  primary: 'text-primary',
  secondary: 'text-muted-foreground'
};

export const MarkdownSpan: ElementType<ComponentProps<'span'> & ExtraProps> = ({
  node,
  className,
  ...props
}) => {
  const color = className ? spanClassToColor[className] : undefined;
  return (
    <span
      {...props}
      className={color}
    />
  );
};

export const MarkdownTableCell: ElementType<
  (ComponentProps<'th'> | ComponentProps<'td'>) & ExtraProps
> = ({ node, ...props }) => {
  const isHeader = node?.tagName === 'th';

  return isHeader ? <TableHead {...props} /> : <TableCell {...props} />;
};
